import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { updatePlan } from '../api/plan';
import RegistarLayout from '../components/RegistarLayout';
import InputFormLayout from '../components/InputFormLayout';
import RegistarButtonLayout from '../components/RegistarButtonLayout';
import SelectLayout from '../components/SelectLayout';
import { planEditSchema } from '../schemas/planEditSchema';
import './PlanRegistar.css';
import { useCoupons } from '../hooks/useCoupons';
import { usePlan } from '../hooks/usePlan';

const defaultValues = {
  name: '',
  amount: '',
  promotion_code: null,
  is_auto_coupon_prefill: false,
  trial_days: '',
};

export default function PlanEditPage() {
  const { companyId, planId } = useParams();
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || `企業ID: ${companyId}`;
  const navigate = useNavigate();

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // クーポン初期化を「1回だけ」に制限するための ref（planId が変わったらリセット）
  const initializedCouponRef = useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(planEditSchema),
    defaultValues,
  });

  const {
    data: planRes,
    error: planFetchError,
    isLoading: planLoading,
  } = usePlan(planId);

  const {
    data: couponsRes,
    error: couponsFetchError,
    isLoading: couponsLoading,
  } = useCoupons();

  // SWRの取得が成功したときのみオプションを生成（初期化はここが揃った後に行う）
  const couponOptions = useMemo(() => {
    if (!couponsRes) return [];
    const list = couponsRes.coupons || [];
    return [
      { label: 'なし', value: null },
      ...list.map((c) => ({
        label: c.name,
        value: c.promotion_code_id,
      })),
    ];
  }, [couponsRes]);

  const fetchErrorMessage = planFetchError
    ? 'プラン情報の取得に失敗しました'
    : couponsFetchError
      ? 'クーポン一覧の取得に失敗しました'
      : '';

  const plan = planRes?.plan ?? null;
  const selectedPromotionCode = watch('promotion_code');

  // 別のプランに切り替えたときに1回だけフラグをリセット
  useEffect(() => {
    initializedCouponRef.current = false;
  }, [planId]);

  useEffect(() => {
    if (!selectedPromotionCode) {
      setValue('is_auto_coupon_prefill', false, { shouldDirty: true });
    }
  }, [selectedPromotionCode, setValue]);

  // プランとクーポン一覧が揃ったら selectedCoupon を初期化（未紐づけなら「なし」）※ref で初回1回のみ
  useEffect(() => {
    if (!plan || couponOptions.length === 0) return;
    if (initializedCouponRef.current) return;

    initializedCouponRef.current = true;
    reset({
      name: plan.name || '',
      amount: String(plan.amount ?? ''),
      promotion_code: plan.promotion_code ?? null,
      is_auto_coupon_prefill: Boolean(plan.is_auto_coupon_prefill),
      trial_days: String(plan.trial_days ?? 0),
    });
  }, [plan, couponOptions, reset]);

  const onSubmit = async (values) => {
    setError('');
    setMessage('');
    if (!window.confirm('編集内容を保存します。よろしいですか？')) {
      return;
    }

    try {
      const payload = {
        name: values.name,
        amount: parseInt(values.amount, 10),
        promotion_code: values.promotion_code,
        is_auto_coupon_prefill: Boolean(values.is_auto_coupon_prefill),
      };

      if (plan.interval === 'month') {
        payload.trial_days = parseInt(values.trial_days || '0', 10);
      }

      await updatePlan(planId, payload);
      setMessage('プランを更新しました！');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'プラン更新に失敗しました');
    }
  };

  if (planLoading || couponsLoading) {
    return (
      <RegistarLayout title="プラン編集" margin="0 auto" marginTop="15vh">
        <div>読み込み中...</div>
      </RegistarLayout>
    );
  }

  if (fetchErrorMessage) {
    return (
      <RegistarLayout title="プラン編集" margin="0 auto" marginTop="15vh">
        <div style={{ color: 'red' }}>{fetchErrorMessage}</div>
      </RegistarLayout>
    );
  }

  if (!plan) {
    return (
      <RegistarLayout title="プラン編集" margin="0 auto" marginTop="15vh">
        <div style={{ color: 'red' }}>プランが見つかりません</div>
      </RegistarLayout>
    );
  }

  const intervalLabel =
    plan.interval === 'one_time' ? '一括払い' : 'サブスクリプション';

  return (
    <RegistarLayout title="プラン編集" margin="0 auto" marginTop="15vh">
      {message && (
        <div style={{ color: 'green', marginBottom: '10px' }}>{message}</div>
      )}
      {error && (
        <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>
      )}

      <div className="planElement">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div style={{ marginBottom: '10px' }}>
              <InputFormLayout
                id="planName"
                labelTitle="プラン名"
                type="text"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder=""
              />
              {errors.name?.message && (
                <div
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                  }}
                >
                  {errors.name.message}
                </div>
              )}
            </div>
          )}
        />

        <Controller
          name="amount"
          control={control}
          render={({ field }) => (
            <div style={{ marginBottom: '10px' }}>
              <InputFormLayout
                id="amount"
                labelTitle="金額（円）"
                type="number"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder=""
              />
              {errors.amount?.message && (
                <div
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                  }}
                >
                  {errors.amount.message}
                </div>
              )}
            </div>
          )}
        />

        {/* クーポン: 選択 */}
        <Controller
          name="promotion_code"
          control={control}
          render={({ field }) => {
            const selectedCoupon =
              couponOptions.find((opt) => opt.value === field.value) ??
              couponOptions.find((o) => o.value === null) ??
              null;
            return (
              <div style={{ marginBottom: '10px' }}>
                <label
                  style={{
                    color: '#333',
                    display: 'block',
                    marginBottom: '5px',
                  }}
                >
                  クーポン
                </label>
                <Autocomplete
                  options={couponOptions}
                  value={selectedCoupon}
                  onChange={(_, newValue) =>
                    field.onChange(newValue?.value ?? null)
                  }
                  getOptionLabel={(option) => option?.label ?? ''}
                  getOptionKey={(option) => option.value ?? 'none'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder="クーポンを検索・選択"
                      error={!!errors.promotion_code}
                      helperText={errors.promotion_code?.message}
                    />
                  )}
                  fullWidth
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                />
              </div>
            );
          }}
        />

        {selectedPromotionCode && (
          <Controller
            name="is_auto_coupon_prefill"
            control={control}
            render={({ field }) => (
              <SelectLayout
                id="is_auto_coupon_prefill"
                selectTitle="クーポン自動フィルイン"
                value={Boolean(field.value) ? '1' : '0'}
                onChange={(e) => field.onChange(e.target.value === '1')}
                options={[
                  { id: 'prefill_off', name: 'しない', value: '0' },
                  { id: 'prefill_on', name: 'する', value: '1' },
                ]}
                show={false}
                required={false}
              />
            )}
          />
        )}

        {/* 課金タイプ: 表示のみ（グレーアウト） */}
        <div style={{ marginBottom: '10px' }}>
          <label
            style={{ color: '#333', display: 'block', marginBottom: '5px' }}
          >
            課金タイプ
          </label>
          <input
            type="text"
            value={intervalLabel}
            disabled
            readOnly
            style={{
              width: '100%',
              padding: '0.7rem 1rem',
              fontSize: '1rem',
              border: '1.5px solid #ccc',
              borderRadius: '6px',
              backgroundColor: '#e9ecef',
              color: '#6c757d',
            }}
          />
        </div>

        {/* 無料トライアル日数: サブスクリプションの場合のみ表示 */}
        {plan.interval === 'month' && (
          <Controller
            name="trial_days"
            control={control}
            render={({ field }) => (
              <div style={{ marginBottom: '10px' }}>
                <label
                  style={{
                    color: '#333',
                    display: 'block',
                    marginBottom: '5px',
                  }}
                >
                  無料トライアル日数
                </label>
                <input
                  type="number"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  min="0"
                  style={{
                    width: '100%',
                    padding: '0.7rem 1rem',
                    fontSize: '1rem',
                    border: '1.5px solid #ccc',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    color: '#333',
                  }}
                />
                {errors.trial_days?.message && (
                  <div
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      marginTop: '4px',
                    }}
                  >
                    {errors.trial_days.message}
                  </div>
                )}
              </div>
            )}
          />
        )}

        <div className="planRegisterButtons">
          <RegistarButtonLayout
            buttonTitle="更新"
            type="submit"
            onClick={handleSubmit(onSubmit)}
          />
          <RegistarButtonLayout
            buttonTitle="プラン一覧に戻る"
            type="button"
            onClick={() =>
              navigate(
                `/plans/${companyId}?companyName=${encodeURIComponent(companyName)}`,
              )
            }
          />
        </div>
      </div>
    </RegistarLayout>
  );
}
